// Fuente única de verdad de las novedades del panel administrativo.
// La alimentan tres consumidores: la página de changelog, los indicadores
// "Nuevo" junto a cada funcionalidad, y el contexto que se inyecta al
// chatbot (incluido "howTo", para que pueda dar pasos concretos).
//
// Para publicar un release nuevo: agrega una entrada al principio del
// arreglo CHANGELOG. No hace falta tocar ningún otro archivo.
//
// Reglas de redacción (no rediscutir por entrada, ya están decididas):
// - Orientado a la tarea que el usuario quiere lograr, no al nombre técnico
//   del módulo ni al archivo que se tocó.
// - Español neutro, segunda persona, sin jerga técnica.
// - Sin relleno ni lenguaje de marketing.
// - No inventar pasos/UI que no se haya verificado contra el código real.
//
// target.route: ruta de react-router-dom donde vive la funcionalidad.
// target.anchor: identificador semántico de la sección/tab dentro de esa
// ruta. HOY NO HAY mecanismo que lea "anchor" desde la URL y salte a esa
// sección — las vistas usan estado interno (tabs por índice), no rutas
// propias. Falta una pieza chica en Fase 3 (leer un hash/query param al
// montar y fijar el índice de tab correspondiente) antes de que los
// tooltips puedan saltar directo a la sección. Mientras tanto "anchor" sirve
// como identificador estable para tracking de "visto" y para que el
// changelog explique en texto dónde encontrar cada cosa.

export const CHANGELOG = [
  {
    id: "2026-10-recuperar-registro-incompleto",
    date: "2026-10-22",
    type: "new",
    title: "Recupera un registro incompleto y márcalo como pagado en efectivo",
    summary:
      "Cuando un atleta llena sus datos de inscripción pero no llega a completar el pago, el registro queda \"incompleto\" en la lista. Ahora puedes recuperarlo desde su detalle: se crea un registro nuevo y completo, con fecha de hoy, pagado en efectivo a tu nombre.",
    details:
      "El registro incompleto original se queda visible en la lista, solo marcado como \"Recuperado\" (quién lo recuperó, cuándo, y a qué registro nuevo dio lugar) — no se borra ni se oculta. El nuevo registro sigue el mismo flujo que cualquier inscripción completa (correo de confirmación incluido), y en los reportes queda atribuido a tu usuario igual que un pago en punto de venta. Si el registro incompleto tiene una ficha OXXO emitida (pagable hasta 48 horas después de emitida), el botón aparece deshabilitado hasta que pasen esas 48 horas, para evitar cobrar dos veces si el atleta paga la ficha en la tienda. El cargo de comisión en línea que agrega la inscripción pública no se incluye en el nuevo registro, ya que el cobro ahora es en efectivo. No disponible para registros que son parte de una inscripción grupal (varios atletas, un solo pago).",
    howTo: [
      "Entra al evento, abre \"Inscripciones\" y busca al atleta en el tab \"Ingresos\" (su estatus dice \"Registro onLine Incompleto\" o \"Pendiente de pago en oxxo\").",
      "Haz clic en su registro para abrir el detalle.",
      "Si no tiene una ficha OXXO vigente, verás el botón \"Recuperar registro\" habilitado — haz clic y confirma.",
      "Si tiene una ficha OXXO emitida hace menos de 48 horas, el botón aparece deshabilitado con la fecha a partir de la cual se habilita.",
    ],
    target: { route: "/eventos-organizados/:EventoIndex/:idregistro", anchor: "recuperar-registro" },
  },
  {
    id: "2026-10-subclasificaciones-valores",
    date: "2026-10-21",
    type: "new",
    title: "Elige y nombra los valores al crear una subclasificación desde un campo de inscripción",
    summary:
      "Al crear una subclasificación a partir de un campo adicional del formulario de inscripción, ahora se listan los valores reales que capturaron tus inscritos (con cuántos tiene cada uno), para que elijas cuáles usar y, si quieres, les pongas un nombre distinto al que se ve en el reporte de resultados.",
    details:
      "Antes, un campo tipo casilla (checkbox) se guardaba como true/false y así aparecía en el reporte, sin decir a qué se refería. Ahora puedes dejar fuera los valores que no te interesan (por ejemplo, que la subclasificación solo aplique a los que marcaron \"true\") y renombrar los que sí incluyes (por ejemplo, mostrar \"Sí\"/\"No\" en vez de true/false). Esto aplica tanto al crear una subclasificación nueva como al editar una ya existente con el botón \"Editar valores\" — al guardar, los resultados ya calculados se vuelven a sincronizar con la nueva configuración.",
    howTo: [
      "Entra al evento, abre \"Configuración de Cronometraje\" y luego el tab \"Subclasificaciones\".",
      "Al crear una nueva, en el origen elige \"De un campo adicional del formulario de inscripción\" y selecciona el campo.",
      "Se muestra la tabla de valores capturados — desmarca \"Usar\" en los que no quieras incluir, y edita el texto de \"Nombre a mostrar\" si quieres que se vea distinto en el reporte.",
      "Para una subclasificación ya creada, usa el botón \"Editar valores\" para ajustar la inclusión o los nombres — al guardar se sincroniza de una vez.",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-subclasificaciones" },
  },
  {
    id: "2026-10-dashboard-reportes-personalizados",
    date: "2026-10-21",
    type: "new",
    title: "Arma tus propios reportes cruzando los datos de inscripción",
    summary:
      "En el Dashboard de inscripciones ya puedes crear reportes personalizados: eliges un campo para agrupar, opcionalmente otro para cruzar en columnas, y filtros — por ejemplo, cuántas mujeres pidieron talla M y cuántos hombres, algo que antes no se podía ver porque cada campo se contaba por separado.",
    details:
      "El resultado se ve en tabla y en gráfico a la vez, y se recalcula siempre contra los datos actuales de inscripción (nunca queda congelado). Puedes guardar varios reportes distintos por evento, cada uno con su nombre, y quedan disponibles cada vez que entras al Dashboard de ese evento — ya no hay que volver a armarlos. También se puede acotar por rango de fecha de inscripción dentro del mismo reporte. Solo se cuentan inscripciones completas (pagadas), igual que el resto del Dashboard.",
    howTo: [
      "Entra al evento, abre \"Inscripciones\" y luego el tab \"Dashboard\".",
      "Busca la sección \"Reportes personalizados\", justo antes de \"Formas de pago\".",
      "Haz clic en \"Generar nuevo reporte\": ponle un nombre, elige el campo para agrupar filas (y opcionalmente otro para columnas), agrega filtros si quieres, y guarda.",
      "Los reportes ya guardados aparecen como botones — haz clic en cualquiera para ver su resultado, o en \"Editar este reporte\" para ajustarlo.",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-dashboard" },
  },
  {
    id: "2026-10-dashboard-dispersion-rango-fechas",
    date: "2026-10-21",
    type: "improvement",
    title: "Acota por fecha el gráfico de dispersión de inscripciones",
    summary:
      "El gráfico de inscripciones por día ahora tiene un control deslizante para elegir el rango de fechas que se ve — útil en eventos con un periodo de inscripción largo, donde antes el gráfico se veía amontonado y difícil de leer.",
    details:
      "Por defecto se ve todo el rango completo de fechas de inscripción; deslizas desde cualquiera de los dos extremos para acotarlo. La línea de \"Inscritos Acumulados\" sigue sumando desde el inicio real del evento aunque acotes el rango visible, para no mostrar un acumulado que empiece en cero a mitad del periodo.",
    howTo: [
      "Entra al evento, abre \"Inscripciones\" y luego el tab \"Dashboard\".",
      "En \"Dispersión de inscripciones\", usa el control deslizante debajo del título para mover la fecha de inicio o la de fin.",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-dashboard" },
  },
  {
    id: "2026-10-editar-orden-compra",
    date: "2026-10-08",
    type: "new",
    title: "Editar los artículos de una orden de compra",
    summary:
      "Desde el detalle de un atleta ahora puedes agregar artículos nuevos, quitar artículos existentes o editar los que ya tiene (inscripción, comisión en línea, productos y sus campos adicionales), en vez de solo poder verlos.",
    details:
      "Solo los perfiles Master y MasterOrganizador pueden editar una orden. La cantidad de la inscripción y de la comisión en línea no se puede modificar (solo su precio); en los productos sí se puede cambiar cantidad, precio y los campos seleccionados (talla, color, etc.). Si el cambio hace que el total de la orden ya no coincida con lo que se cobró originalmente, el sistema te pide escribir una nota explicando la diferencia antes de guardar — esa nota se agrega como un artículo más en la orden para que el total siga cuadrando con lo realmente pagado. Todos los cambios quedan en el historial de cambios del atleta, y además hay un listado con todos los cambios de órdenes de todo el evento en la configuración del evento, tab \"Autorizaciones\", justo debajo de la lista de folios borrados.",
    howTo: [
      "Entra al detalle de un atleta y abre el tab \"Detalle Pago\".",
      "Haz clic en \"Editar\" dentro de la orden de compra.",
      "Agrega un producto nuevo, quita un artículo existente, o edita cantidad/precio/campos de uno ya existente.",
      "Si el total cambia respecto al que estaba, escribe la nota que te pide el sistema explicando la diferencia y guarda.",
      "Para ver todos los cambios de órdenes del evento juntos, entra a la configuración del evento, tab \"Autorizaciones\", y revisa la lista debajo de \"Folios borrados\".",
    ],
    target: { route: "/eventos-organizados/:EventoIndex/:idregistro", anchor: "detalle-pago" },
  },
  {
    id: "2026-10-alertas-edad-dorsal",
    date: "2026-10-08",
    type: "new",
    title: "Alertas cuando la edad o el dorsal de un atleta no corresponden a los rangos definidos",
    summary:
      "El sistema avisa si la edad calculada de un atleta no corresponde al rango de la categoría en la que está inscrito, o si su dorsal asignado queda fuera del rango definido para su modalidad.",
    details:
      "En el detalle del atleta se muestra la edad calculada para el evento (según la fecha de corte configurada) junto a la fecha de nacimiento, y un ícono de alerta junto a la categoría o el dorsal cuando no corresponden al rango esperado — haz clic en el ícono para ver el detalle del problema. En el listado de inscritos (tab \"Ingresos\") se agregó una columna \"Alertas\" con esta misma validación, que se puede filtrar para ver solo a los atletas con algún problema.",
    howTo: [
      "Entra al detalle de un atleta: junto a la fecha de nacimiento verás su edad calculada para el evento.",
      "Si la categoría asignada no corresponde a esa edad, o el dorsal está fuera del rango de su modalidad, aparece un ícono de alerta junto al dato correspondiente — haz clic para ver el mensaje.",
      "En el tab \"Ingresos\" del evento, usa el filtro de la columna \"Alertas\" para ver solo a los atletas marcados \"Con alerta\".",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-ingresos" },
  },
  {
    id: "2026-10-inscripciones-grupales",
    date: "2026-10-06",
    type: "new",
    title: "Tus atletas ya pueden inscribir a varios participantes con un solo pago",
    summary:
      "Al inscribirse en línea, un atleta puede agregar otros participantes (familiares, amigos, equipo) antes de pagar, y se hace un solo pago que cubre a todo el grupo — no es necesario que cada uno pague por separado.",
    details:
      "Esta función vive en el formulario público de inscripción (no en el panel administrativo), así que no hay una pantalla para \"administrar grupos\". Lo que sí verás como admin: cada acompañante sigue apareciendo como un inscrito normal y separado en tu listado — con su propio folio y su propio correo de confirmación — pero su monto cobrado se ve en $0, porque el total del grupo completo (incluyendo la comisión en línea) se refleja en el registro del primer atleta que inició la inscripción (el \"líder\" del grupo). Si necesitas rastrear a qué pago pertenece un acompañante, su registro guarda una referencia al inscrito líder.",
    howTo: [
      "El atleta llena sus datos y, antes de pagar, puede elegir \"Agregar otro participante\" para sumar a alguien más al mismo pago (sin límite de cuántos).",
      "Cuando ya agregó a todos, elige \"Continuar con el pago\" y se cobra el total combinado de todo el grupo en un solo pago.",
      "Cada participante del grupo recibe su propia confirmación y puede ver también la confirmación de los demás miembros del grupo.",
    ],
    target: null,
  },
  {
    id: "2026-10-listado-inscritos-filtros",
    date: "2026-10-06",
    type: "improvement",
    title: "Ordenar y filtrar el listado de inscritos por cualquier columna",
    summary:
      "Haz clic en cualquier encabezado de columna para ordenar por ese dato, y usa el filtro debajo de cada columna para buscar valores específicos — incluye listas desplegables con las opciones reales para modalidad, categoría, género, usuario y estatus, y para los campos del formulario que son de tipo selección.",
    details:
      "Las filas también son más compactas (menos espacio en blanco arriba y abajo de cada una). El filtro de estatus (inscripciones completas/incompletas o borradas/todas) ahora son botones en vez de un menú desplegable, y se agregó un botón para mostrar toda la lista de inscritos de una vez sin tener que ir cargando de a poco.",
    howTo: [
      "Entra al evento y abre el tab \"Ingresos\".",
      "Haz clic en el nombre de cualquier columna para ordenar por ese dato; vuelve a hacer clic para invertir el orden.",
      "Escribe o selecciona un valor en el campo debajo de cada columna para filtrar — puedes combinar varios filtros a la vez.",
      "Usa los botones de arriba para elegir qué inscripciones ver, o para mostrar la lista completa.",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-ingresos" },
  },
  {
    id: "2026-10-codigos-lote-vigencia",
    date: "2026-10-06",
    type: "new",
    title: "Crear varios códigos de descuento a la vez y definirles una fecha límite",
    summary:
      "Al crear un código personalizado ahora puedes escribir varios separados por coma para generarlos todos juntos en el mismo grupo, y opcionalmente ponerles una fecha después de la cual dejan de funcionar.",
    details:
      "La tabla de cada grupo ahora muestra esa fecha límite (o \"No definida\" si no se configuró). Al terminar de crear códigos, el diálogo se cierra solo y te confirma cuántos se generaron, sin tener que adivinar si la operación funcionó.",
    howTo: [
      "Entra a la configuración del evento, abre el tab \"Códigos de descuento\" y crea un nuevo grupo con código personalizado.",
      "En el campo del nombre del código, escribe varios separados por coma (ej. DESC20,DESC21,DESC22) para crearlos todos de una vez.",
      "Si quieres que dejen de funcionar después de cierta fecha, complétala en \"Fecha límite de vigencia\" — si la dejas vacía, el código no tiene límite.",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-descuentos" },
  },
  {
    id: "2026-09-folios-dorsales",
    date: "2026-09-30",
    type: "new",
    title: "Administrar y reservar folios (dorsales) de tu evento",
    summary:
      "Ve de un vistazo qué folios ya están asignados, cuáles están libres, y reserva un folio específico para que el sistema no se lo entregue a nadie hasta que tú lo decidas.",
    details:
      "Dentro de la configuración del evento, la sección de folios ahora muestra un resumen por modalidad (cuántos folios hay, cuántos asignados, reservados y libres), una matriz visual con un color por estado, y un buscador para abrir un folio específico. Si un folio no tiene dueño, puedes reservarlo/bloquearlo para que la asignación automática lo salte. También detecta folios duplicados (un mismo atleta con más de uno) y folios asignados fuera del rango de su modalidad, con un botón para corregirlos.",
    howTo: [
      "Entra a la configuración del evento y abre el tab \"Dorsales o folios\".",
      "Arriba verás el resumen por modalidad y la matriz de folios: verde = asignado, naranja = reservado, gris = libre.",
      "Usa el buscador para escribir un número de folio específico y ver su estado.",
      "Haz clic en cualquier folio: si tiene dueño se abre el detalle del atleta; si no tiene dueño se abre la opción de reservarlo o bloquearlo.",
      "Si hay folios duplicados o fuera de rango, aparecen en una tabla aparte con un botón para liberarlos.",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-dorsal" },
  },
  {
    id: "2026-09-etiquetas-imprimibles",
    date: "2026-09-30",
    type: "new",
    title: "Generar etiquetas imprimibles para pegar en los dorsales físicos",
    summary:
      "Genera un PDF con las etiquetas de tus participantes, eligiendo a quién incluir y el formato exacto de la hoja donde las vas a imprimir.",
    details:
      "Filtra y selecciona a los participantes que quieres imprimir, elige qué datos van en cada etiqueta (con la posibilidad de combinar varios, como modalidad y categoría), y define el formato de la hoja: columnas, filas, márgenes y separación entre etiquetas, con vista previa antes de generar el PDF.",
    howTo: [
      "Entra a la configuración del evento y abre el tab \"Etiquetas\".",
      "Selecciona a los participantes que quieres incluir.",
      "Elige qué atributos mostrar en cada etiqueta y ajusta el formato de la hoja.",
      "Revisa la vista previa y genera el PDF.",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-etiquetas" },
  },
  {
    id: "2026-09-logs-del-sistema",
    date: "2026-09-20",
    type: "new",
    title: "Ver el historial de cambios y quién los hizo (Logs del sistema)",
    summary:
      "Consulta un registro de los cambios realizados en tu evento y qué usuario los hizo, útil para auditar ajustes hechos por tu equipo.",
    details: null,
    howTo: [
      "Entra a la configuración del evento y abre el tab \"Autorizaciones\".",
      "Haz clic en el botón \"Logs del sistema\" (solo visible para perfiles Master/Master Organizador).",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-autorizaciones-logs" },
  },
  {
    id: "2026-09-deslinde-personalizable",
    date: "2026-09-22",
    type: "new",
    title: "Personalizar el texto del deslinde de responsabilidades",
    summary:
      "Edita el texto legal del deslinde que firman tus atletas, con la opción de insertar variables como el nombre del evento para que se rellenen automáticamente.",
    details:
      "Si no lo personalizas, se usa el texto por defecto de Dashport. Hay una versión para mayores de edad y otra para menores de edad con un padre/tutor como firmante.",
    howTo: [
      "Entra a la configuración del evento y abre el tab \"Deslinde de responsabilidades\".",
      "Edita el texto y usa variables como {nombreEvento} donde quieras que se reemplacen automáticamente.",
      "Guarda los cambios.",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-deslinde" },
  },
  {
    id: "2026-09-inventario",
    date: "2026-09-22",
    type: "new",
    title: "Controlar existencias de tallas y productos adicionales",
    summary:
      "Define cuántas unidades hay disponibles de cada talla u opción, y de tus productos adicionales; el sistema descuenta automáticamente al pagar y libera espacio si se borra un registro.",
    details:
      "Cuando una opción se agota queda deshabilitada en el formulario de inscripción (o el producto se marca como agotado) para que nadie la pueda seguir seleccionando.",
    howTo: [
      "Entra a la configuración del evento y abre el tab \"Inventarios\".",
      "Crea o edita un ítem de inventario y define la capacidad disponible por opción.",
      "El sistema descuenta automáticamente cada vez que alguien paga por esa opción.",
    ],
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "tab-inventarios" },
  },
  {
    id: "2026-09-cronometraje-linea-tiempo",
    date: "2026-09-20",
    type: "new",
    title: "Ver la línea de tiempo de puntos de control por modalidad",
    summary:
      "Visualiza, para cada distancia de tu evento, en qué kilómetro está ubicado cada punto de control de cronometraje.",
    details: null,
    howTo: null,
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "cronometraje-linea-tiempo" },
  },
  {
    id: "2026-09-planificar-modalidades-crono",
    date: "2026-09-20",
    type: "new",
    title: "Planificar cómo se agrupan las modalidades para cronometraje",
    summary:
      "Decide, para cada modalidad de inscripción, si debe cronometrarse tal cual, excluirse, o agruparse con otras — y qué categorías deben separarse en su propia modalidad de crono.",
    details:
      "Al aplicar los cambios, la configuración de cronometraje se reemplaza por completo con lo que definas en esta pantalla.",
    howTo: null,
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "cronometraje-planificar-modalidades" },
  },
  {
    id: "2026-09-ceremonia-premiacion",
    date: "2026-09-20",
    type: "new",
    title: "Mostrar una pantalla de ceremonia de premiación",
    summary:
      "Proyecta una vista pensada para anunciar a los ganadores de cada categoría en pantalla, durante la ceremonia de premiación del evento.",
    details: null,
    howTo: null,
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "reportes-ceremonia-premiacion" },
  },
  {
    id: "2026-09-tablero-ganadores",
    date: "2026-09-20",
    type: "new",
    title: "Tablero compacto de ganadores",
    summary:
      "Una vista resumida con los ganadores del evento, pensada para compartir o mostrar en pantalla de forma más compacta que el reporte completo de resultados.",
    details: null,
    howTo: null,
    target: { route: "/eventos-organizados/:EventoIndex", anchor: "reportes-tablero-ganadores" },
  },
];

export function getChangelogById(id) {
  return CHANGELOG.find((entry) => entry.id === id) || null;
}

// Los anchors ya son identificadores únicos en toda la app (no hay dos
// secciones distintas con el mismo anchor), así que alcanza con filtrar por
// anchor solo — route siempre es la misma plantilla de ruta hoy.
export function getEntriesForAnchor(anchor) {
  if (!anchor) return [];
  return CHANGELOG.filter((entry) => entry.target && entry.target.anchor === anchor);
}

// Agrupa las entradas por mes/año (ej. "2026-09" -> [...]) en orden
// cronológico inverso, ya que el proyecto no tiene un esquema de versionado
// real (package.json de beta/dashportV6.0 nunca pasó de "0.1.0").
export function groupChangelogByMonth() {
  const grupos = new Map();
  [...CHANGELOG]
    .sort((a, b) => b.date.localeCompare(a.date))
    .forEach((entry) => {
      const clave = entry.date.slice(0, 7); // "2026-09"
      if (!grupos.has(clave)) grupos.set(clave, []);
      grupos.get(clave).push(entry);
    });
  return Array.from(grupos.entries()).map(([mes, entries]) => ({ mes, entries }));
}



